import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Play, Save, Folder, Plus, Settings, Workflow, Clock, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { TOOL_METADATA, canConnect, getAllTools } from '../constants/toolGraph';
import { taskGroupsAPI, inputSourcesAPI, tasksAPI, scanTemplatesAPI } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import CustomFlagsInput from '../components/CustomFlagsInput';
import MongoQueryBuilder from '../components/MongoQueryBuilder';

// Custom node component
const ToolNode = ({ data }) => {
  const metadata = TOOL_METADATA[data.tool];
  
  return (
    <div style={{
      padding: '16px',
      border: `2px solid ${metadata.color}`,
      borderRadius: '12px',
      backgroundColor: 'var(--bg-secondary)',
      minWidth: '180px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      position: 'relative',
    }}>
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          width: '12px',
          height: '12px',
          backgroundColor: metadata.color,
          border: '2px solid white',
        }}
      />
      
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
      }}>
        <div style={{ fontSize: '24px' }}>{metadata.icon}</div>
        <div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
            {metadata.name}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
            {data.tool}
          </div>
        </div>
      </div>
      
      {data.status && (
        <div style={{
          fontSize: '11px',
          padding: '4px 8px',
          borderRadius: '4px',
          backgroundColor: data.status === 'completed' ? '#10b981' : '#3b82f6',
          color: 'white',
          textAlign: 'center',
          marginTop: '8px',
        }}>
          {{
            pending: 'ожидает',
            running: 'выполняется',
            completed: 'завершён',
            failed: 'ошибка',
            cancelled: 'отменён',
          }[data.status] || data.status}
        </div>
      )}
      
      {Object.keys(data.options || {}).length > 0 && (
        <div style={{
          fontSize: '10px',
          color: 'var(--text-secondary)',
          marginTop: '8px',
          padding: '4px',
          backgroundColor: 'var(--bg-tertiary)',
          borderRadius: '4px',
        }}>
          {Object.keys(data.options).length} флаг(ов)
        </div>
      )}
      
      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        style={{
          width: '12px',
          height: '12px',
          backgroundColor: metadata.color,
          border: '2px solid white',
        }}
      />
    </div>
  );
};

const UniversalScan = () => {
  const { showSuccess, showError, showInfo } = useNotification();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  
  // Ref to track current nodes state for keyboard event handler
  const nodesRef = useRef([]);
  const [showNodeConfig, setShowNodeConfig] = useState(false);
  const [nodeOptions, setNodeOptions] = useState({});
  const [pipelineName, setPipelineName] = useState('');
  const [executing, setExecuting] = useState(false);
  const [initialInput, setInitialInput] = useState('');
  
  // Input source management
  const [inputSourceType, setInputSourceType] = useState('manual');
  const [inputSources, setInputSources] = useState(null);
  const [selectedPipeline, setSelectedPipeline] = useState('');
  const [selectedTool, setSelectedTool] = useState('');
  const [loadingSources, setLoadingSources] = useState(false);
  
  // Config.json variants
  const [configSubtype, setConfigSubtype] = useState('domains'); // domains, raw, links
  const [selectedTags, setSelectedTags] = useState('');

  // Template management
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [templateAction, setTemplateAction] = useState('save'); // 'save' or 'update'
  const [templateSchedule, setTemplateSchedule] = useState({ enabled: false, type: 'interval', expr: '24h' });
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Confirmation dialog for pipeline execution
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pipelineToExecute, setPipelineToExecute] = useState(null);

  // Scheduled pipelines panel
  const [showScheduledPanel, setShowScheduledPanel] = useState(false);
  const [scheduledTemplates, setScheduledTemplates] = useState([]);

  // MongoDB query builder modal
  const [showMongoModal, setShowMongoModal] = useState(false);
  const [lastMongoQuery, setLastMongoQuery] = useState(null); // Stores mongo query params for template saving

  // Define node types - memoized to prevent unnecessary re-renders
  const nodeTypes = useMemo(() => ({
    toolNode: ToolNode,
  }), []);

  // Add new tool node
  const addToolNode = (tool) => {
    const newNode = {
      id: `${tool}-${Date.now()}`,
      type: 'toolNode',
      position: { x: Math.random() * 400 + 100, y: Math.random() * 300 + 100 },
      data: { 
        tool,
        options: {},
        label: TOOL_METADATA[tool].name,
      },
    };
    setNodes((nds) => nds.concat(newNode));
  };

  // Delete node and its connections (making it stable with useCallback)
  const deleteNode = useCallback((nodeId) => {
    setNodes((nds) => nds.filter((node) => node.id !== nodeId));
    setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
    
    // Close node config if the deleted node was selected
    if (selectedNode && selectedNode.id === nodeId) {
      setShowNodeConfig(false);
      setSelectedNode(null);
    }
    
    showSuccess('Узел успешно удалён');
  }, [selectedNode, setNodes, setEdges, showSuccess]); // Dependencies added

  // Load input sources
  const loadInputSources = async () => {
    try {
      setLoadingSources(true);
      const response = await inputSourcesAPI.getInputSources();
      setInputSources(response.data);
    } catch (err) {
      showError('Не удалось загрузить источники входных данных');
    } finally {
      setLoadingSources(false);
    }
  };

  // Handle input source type change
  const handleInputSourceChange = (type) => {
    setInputSourceType(type);
    setSelectedPipeline('');
    setSelectedTool('');
    setLastMongoQuery(null);

    if (type === 'manual') {
      setInitialInput('');
    } else if (type === 'config') {
      // Will be loaded based on subtype and tags
      loadConfigData(configSubtype, selectedTags);
    }
  };

  // Load config data based on subtype and tags
  const loadConfigData = async (subtype, tags) => {
    try {
      const response = await inputSourcesAPI.getConfigData(subtype, tags);
      const data = response.data.data || [];
      
      if (subtype === 'raw') {
        // For raw config, stringify each object
        setInitialInput(data.map(item => JSON.stringify(item)).join('\n'));
      } else {
        // For domains and links, just join strings
        setInitialInput(data.join('\n'));
      }
    } catch (err) {
      showError('Не удалось загрузить данные конфигурации');
    }
  };

  // Handle config subtype change
  const handleConfigSubtypeChange = (subtype) => {
    setConfigSubtype(subtype);
    if (inputSourceType === 'config') {
      loadConfigData(subtype, selectedTags);
    }
  };

  // Handle tags change
  const handleTagsChange = (tags) => {
    setSelectedTags(tags);
    if (inputSourceType === 'config') {
      loadConfigData(configSubtype, tags);
    }
  };

  // Handle pipeline selection
  const handlePipelineChange = (pipelineId) => {
    setSelectedPipeline(pipelineId);
    setSelectedTool('');
    setInitialInput('');
  };

  // Handle tool selection
  const handleToolChange = (tool) => {
    setSelectedTool(tool);
    
    if (selectedPipeline && tool) {
      const pipeline = inputSources.pipelines.find(p => p.group_id === selectedPipeline);
      if (pipeline) {
        const task = pipeline.tasks.find(t => t.tool === tool);
        if (task) {
          // Load the actual data from the result
          loadToolResultData(task.result_id);
        }
      }
    }
  };

  // Load tool result data
  const loadToolResultData = async (resultId) => {
    try {
      const response = await tasksAPI.getTaskResult(resultId);
      const result = response.data;
      
      // Send raw data as-is without transformation
      let rawData = [];
      if (result.raw_data && Array.isArray(result.raw_data)) {
        // Use raw_data if available (contains full objects with metadata)
        rawData = result.raw_data;
      } else if (result.data && Array.isArray(result.data)) {
        // Fallback to data field
        rawData = result.data;
      }
      
      // Convert to string format for textarea
      if (rawData.length > 0) {
        if (typeof rawData[0] === 'string') {
          setInitialInput(rawData.join('\n'));
        } else {
          // For objects, stringify each one
          setInitialInput(rawData.map(item => JSON.stringify(item)).join('\n'));
        }
      }
    } catch (err) {
      showError('Не удалось загрузить данные результата инструмента');
    }
  };

  // Load templates
  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await scanTemplatesAPI.getScanTemplates();
      const all = response.data.templates || [];
      setTemplates(all);
      setScheduledTemplates(all.filter(t => t.schedule?.enabled));
    } catch (err) {
      showError('Не удалось загрузить шаблоны');
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Handle template selection
  const handleTemplateChange = async (templateId) => {
    if (!templateId) {
      setSelectedTemplate('');
      return;
    }

    try {
      const response = await scanTemplatesAPI.getScanTemplate(templateId);
      const template = response.data;
      
      setNodes(template.nodes || []);
      setEdges(template.edges || []);
      setSelectedTemplate(templateId);
      setTemplateName(template.name);

      // Restore input_config if saved in template
      const ic = template.input_config;
      if (ic) {
        if (ic.type === 'manual') {
          setInputSourceType('manual');
          setInitialInput((ic.content || []).join('\n'));
          setLastMongoQuery(null);
        } else if (ic.type === 'config') {
          setInputSourceType('config');
          setConfigSubtype(ic.subtype || 'domains');
          setSelectedTags(ic.tags || '');
          loadConfigData(ic.subtype || 'domains', ic.tags || '');
          setLastMongoQuery(null);
        } else if (ic.type === 'mongo') {
          setInputSourceType('manual');
          setLastMongoQuery({ mode: ic.mode, collection: ic.collection, filter: ic.filter, projection: ic.projection, pipeline: ic.pipeline, jq_filter: ic.jq_filter });
          // Load data from mongo query for preview
          setInitialInput('(MongoDB-запрос будет выполнен при запуске)');
        }
      }

      showSuccess('Шаблон успешно загружен');
    } catch (err) {
      showError('Не удалось загрузить шаблон');
    }
  };

  // Save template
  const handleSaveTemplate = () => {
    if (nodes.length === 0) {
      showError('Добавьте хотя бы один инструмент, чтобы сохранить шаблон');
      return;
    }
    
    setTemplateAction('save');
    setTemplateName('');
    setShowTemplateDialog(true);
  };

  // Update template
  const handleUpdateTemplate = () => {
    if (!selectedTemplate) {
      showError('Не выбран шаблон для обновления');
      return;
    }
    
    setTemplateAction('update');
    setShowTemplateDialog(true);
  };

  // Delete template
  const handleDeleteTemplate = async () => {
    if (!selectedTemplate) {
      showError('Не выбран шаблон для удаления');
      return;
    }

    if (!window.confirm('Удалить этот шаблон?')) {
      return;
    }

    try {
      await scanTemplatesAPI.deleteScanTemplate(selectedTemplate);
      showSuccess('Шаблон успешно удалён');
      
      // Clear selection and reload templates
      setSelectedTemplate('');
      setTemplateName('');
      setNodes([]);
      setEdges([]);
      await loadTemplates();
    } catch (err) {
      showError('Не удалось удалить шаблон');
    }
  };

  // Confirm template action
  const handleConfirmTemplateAction = async () => {
    if (!templateName.trim()) {
      showError('Нужно указать имя шаблона');
      return;
    }

    try {
      // Build input_config based on current input source type
      let inputConfig;
      if (lastMongoQuery) {
        inputConfig = { type: 'mongo', ...lastMongoQuery };
      } else if (inputSourceType === 'config') {
        inputConfig = { type: 'config', subtype: configSubtype, tags: selectedTags };
      } else {
        inputConfig = { type: 'manual', content: initialInput.split('\n').filter(line => line.trim()) };
      }

      const templateData = {
        name: templateName.trim(),
        nodes,
        edges,
        schedule: templateSchedule,
        input_config: inputConfig,
      };

      if (templateAction === 'save') {
        await scanTemplatesAPI.createScanTemplate(templateData);
        showSuccess('Шаблон успешно сохранён');
      } else {
        await scanTemplatesAPI.updateScanTemplate(selectedTemplate, templateData);
        showSuccess('Шаблон успешно обновлён');
      }

      setShowTemplateDialog(false);
      await loadTemplates();
    } catch (err) {
      showError(err.response?.data?.message || 'Не удалось сохранить шаблон');
    }
  };

  // Sync nodesRef with current nodes state
  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  // Load input sources on component mount
  useEffect(() => {
    loadInputSources();
    loadTemplates();
  }, []);

  // Handle keyboard events for node deletion
  const handleKeyDown = useCallback((event) => {
    // Don't handle keyboard events if modal is open
    if (showNodeConfig || showTemplateDialog || showConfirmDialog) {
      return;
    }
    
    // Don't delete if user is typing in an input or textarea
    const activeElement = document.activeElement;
    if (activeElement && (
      activeElement.tagName === 'INPUT' || 
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.isContentEditable
    )) {
      return;
    }
    
    if (event.key === 'Backspace' || event.key === 'Delete') {
      const selectedNodesToDel = nodesRef.current.filter(n => n.selected);
      
      if (selectedNodesToDel.length > 0) {
        selectedNodesToDel.forEach(node => deleteNode(node.id));
        event.preventDefault();
      }
    }
  }, [showNodeConfig, showTemplateDialog, showConfirmDialog, deleteNode]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Handle connection between nodes
  const onConnect = useCallback(
    (params) => {
      // Find source and target nodes
      const sourceNode = nodes.find((n) => n.id === params.source);
      const targetNode = nodes.find((n) => n.id === params.target);
      
      if (!sourceNode || !targetNode) return;
      
      // Check if connection is valid
      const sourceTool = sourceNode.data.tool;
      const targetTool = targetNode.data.tool;
      
      if (!canConnect(sourceTool, targetTool)) {
        showError(`Нельзя связать ${sourceTool} с ${targetTool}`);
        return;
      }
      
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: '#3b82f6',
            },
            style: { stroke: '#3b82f6', strokeWidth: 2 },
          },
          eds
        )
      );
    },
    [nodes, setEdges, showError]
  );

  // Handle node click for selection only
  const onNodeClick = useCallback((event, node) => {
    // Single click just selects the node (ReactFlow handles selection internally)
    setSelectedNode(node);
  }, []);

  // Handle double click to open configuration
  const onNodeDoubleClick = useCallback((event, node) => {
    setSelectedNode(node);
    setNodeOptions(node.data.options || {});
    setShowNodeConfig(true);
  }, []);

  // Save node configuration
  const handleSaveNodeConfig = () => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === selectedNode.id) {
          return {
            ...node,
            data: {
              ...node.data,
              options: nodeOptions,
            },
          };
        }
        return node;
      })
    );
    setShowNodeConfig(false);
    showSuccess('Конфигурация узла сохранена');
  };

  // Prepare pipeline for execution (validation and building)
  const handleExecutePipeline = async () => {
    if (nodes.length === 0) {
      showError('Добавьте хотя бы один инструмент в пайплайн');
      return;
    }

    // Find root nodes (nodes without incoming edges)
    const nodeIds = new Set(nodes.map((n) => n.id));
    const targetIds = new Set(edges.map((e) => e.target));
    const rootNodes = nodes.filter((n) => !targetIds.has(n.id));

    if (rootNodes.length === 0) {
      showError('У пайплайна нет стартовой точки (у всех узлов есть вход)');
      return;
    }

    if (rootNodes.length > 1) {
      showError('У пайплайна должна быть ровно одна стартовая точка');
      return;
    }

    if (!initialInput.trim()) {
      showError('Укажите исходные входные данные');
      return;
    }

    // Build pipeline in topological order
    const pipeline = [];
    const visited = new Set();
    const temp = new Set();
    const nodeIdToTempId = {}; // Map node IDs to temporary unique IDs

    const visit = (nodeId) => {
      if (temp.has(nodeId)) {
        throw new Error('Обнаружена циклическая зависимость');
      }
      if (visited.has(nodeId)) {
        return;
      }

      temp.add(nodeId);
      
      // Find outgoing edges
      const outEdges = edges.filter((e) => e.source === nodeId);
      for (const edge of outEdges) {
        visit(edge.target);
      }

      temp.delete(nodeId);
      visited.add(nodeId);
      
      const node = nodes.find((n) => n.id === nodeId);
      
      // Determine if this is the root node (first in pipeline)
      const isRootNode = nodeId === rootNodes[0].id;
      
      // Find the source tool and parent node ID for chained tasks
      let sourceTool = null;
      let parentNodeId = null;
      
      if (!isRootNode) {
        // Find incoming edges to determine source tool and parent
        const inEdges = edges.filter((e) => e.target === nodeId);
        if (inEdges.length > 0) {
          parentNodeId = inEdges[0].source; // Use first incoming edge
          const sourceNode = nodes.find((n) => n.id === parentNodeId);
          if (sourceNode) {
            sourceTool = sourceNode.data.tool;
          }
        }
      }
      
      // Override source_tool based on input source type
      if (isRootNode) {
        // For root node, source_tool depends on input source type
        console.log('Корневой узел - inputSourceType:', inputSourceType, 'selectedTool:', selectedTool);
        
        if (inputSourceType === 'tool' && selectedTool) {
          // Previous Pipeline → Manual: source_tool = selectedTool
          sourceTool = selectedTool;
          console.log('Устанавливаем source_tool в selectedTool:', sourceTool);
        } else if (inputSourceType === 'manual' || inputSourceType === 'config') {
          // Manual/Config → Manual: source_tool = null
          sourceTool = null;
          console.log('Устанавливаем source_tool в null для manual/config входа');
        } else {
          // Fallback: no source_tool
          sourceTool = null;
          console.log('Устанавливаем source_tool в null (резервный вариант)');
        }
      }
      
      // Generate a temporary unique ID for this task
      const tempId = `temp_${nodeId}`;
      nodeIdToTempId[nodeId] = tempId;
      
      const taskConfig = {
        temp_id: tempId, // Temporary ID for this task
        parent_temp_id: parentNodeId ? nodeIdToTempId[parentNodeId] : null, // Temporary ID of parent
        tool: node.data.tool,
        options: node.data.options || {},
        input_data: isRootNode ? {
          type: (inputSourceType === 'tool' && selectedTool) ? 'pipeline' : 'manual',
          content: initialInput.split('\n').filter((line) => line.trim()),
          source_tool: sourceTool,
        } : {
          type: 'chained',
          content: [],
          source_tool: sourceTool,
        },
        source_tool: sourceTool,
      };
      
      pipeline.unshift(taskConfig);
    };

    try {
      visit(rootNodes[0].id);
      
      // Now convert temp IDs to actual pipeline indices
      const tempIdToIndex = {};
      pipeline.forEach((step, index) => {
        tempIdToIndex[step.temp_id] = index;
      });
      
      // Set parent_task_index for each task
      pipeline.forEach((step, index) => {
        if (step.parent_temp_id) {
          step.parent_task_index = tempIdToIndex[step.parent_temp_id];
          step.input_data.parent_task_index = step.parent_task_index;
        }
        // Clean up temporary IDs
        delete step.temp_id;
        delete step.parent_temp_id;
      });
      
      console.log('Итоговый пайплайн:', JSON.stringify(pipeline, null, 2));
      console.log('Сводка пайплайна:');
      pipeline.forEach((step, index) => {
        console.log(`Step ${index}: ${step.tool}, type: ${step.input_data.type}, source_tool: ${step.source_tool}, parent_index: ${step.parent_task_index}`);
      });
      
      // Store pipeline and show confirmation dialog
      setPipelineToExecute(pipeline);
      setShowConfirmDialog(true);
      
    } catch (err) {
      if (err.message) {
        showError(err.message);
      } else {
        showError('Не удалось собрать пайплайн');
      }
    }
  };

  // Actually execute the pipeline after confirmation
  const confirmExecutePipeline = async () => {
    if (!pipelineToExecute) return;
    
    try {
      setExecuting(true);
      setShowConfirmDialog(false);
      
      const response = await taskGroupsAPI.createGroup({
        name: pipelineName || `Пайплайн ${new Date().toLocaleString()}`,
        pipeline: pipelineToExecute,
      });

      showSuccess('Выполнение пайплайна запущено');
      showInfo(`ID группы: ${response.data.group_id}`);
      
      // Clear the canvas and pipeline data
      setNodes([]);
      setEdges([]);
      setInitialInput('');
      setPipelineName('');
      setPipelineToExecute(null);
      
    } catch (err) {
      showError(err.response?.data?.message || 'Не удалось запустить пайплайн');
    } finally {
      setExecuting(false);
    }
  };

  // Save pipeline template to file
  const handleExportTemplate = () => {
    const template = {
      name: pipelineName || 'Безымянный пайплайн',
      nodes,
      edges,
      createdAt: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(template, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `pipeline-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);

    showSuccess('Шаблон пайплайна сохранён');
  };

  // Load pipeline template
  const handleLoadTemplate = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const template = JSON.parse(e.target.result);
        setNodes(template.nodes || []);
        setEdges(template.edges || []);
        setPipelineName(template.name || '');
        showSuccess('Шаблон пайплайна загружен');
      } catch (err) {
        showError('Не удалось загрузить шаблон');
      }
    };
    reader.readAsText(file);
  };

  return (
    <div style={{ padding: '24px', height: 'calc(100vh - 48px)' }}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: '16px' }}>
        <div className="page-header-icon">
          <Workflow size={24} />
        </div>
        <div className="page-header-text">
          <h1>Сканирование</h1>
          <p>Визуальная сборка и запуск пайплайнов</p>
        </div>
      </div>

      {/* Controls */}
      <div className="card" style={{ padding: '16px', marginBottom: '16px' }}>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '16px',
          alignItems: 'center',
        }}>
          <input
            type="text"
            placeholder="Название пайплайна (необязательно)"
            value={pipelineName}
            onChange={(e) => setPipelineName(e.target.value)}
            style={{
              flex: '1',
              minWidth: '200px',
              padding: '8px 12px',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
            }}
          />
          
          <button
            onClick={handleExecutePipeline}
            disabled={executing || nodes.length === 0}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: '#10b981',
              color: 'white',
              cursor: executing || nodes.length === 0 ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: executing || nodes.length === 0 ? 0.5 : 1,
            }}
          >
            <Play size={16} />
            {executing ? 'Запуск...' : 'Выполнить'}
          </button>
          
          {/* Template Management */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select
              value={selectedTemplate}
              onChange={(e) => handleTemplateChange(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
                minWidth: '150px',
              }}
            >
              <option value="">Загрузить шаблон...</option>
              {templates.map((template) => (
                <option key={template._id} value={template._id}>
                  {template.name}{template.schedule?.enabled ? ' [авто]' : ''}
                </option>
              ))}
            </select>

            <button
              onClick={handleSaveTemplate}
              disabled={nodes.length === 0}
              style={{
                padding: '8px 16px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
                cursor: nodes.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: nodes.length === 0 ? 0.5 : 1,
              }}
            >
              <Save size={16} />
              Сохранить шаблон
            </button>

            <button
              onClick={handleExportTemplate}
              disabled={nodes.length === 0}
              style={{
                padding: '8px 16px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
                cursor: nodes.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: nodes.length === 0 ? 0.5 : 1,
              }}
            >
              <Folder size={16} />
              Экспорт шаблона
            </button>

            {selectedTemplate && (
              <>
                <button
                  onClick={handleUpdateTemplate}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <Save size={16} />
                  Обновить
                </button>

                <button
                  onClick={handleDeleteTemplate}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #ef4444',
                    borderRadius: '6px',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  ×
                </button>
              </>
            )}
          </div>
          
          <label style={{
            padding: '8px 16px',
            border: '1px solid var(--border-color)',
            borderRadius: '6px',
            backgroundColor: 'var(--bg-tertiary)',
            color: 'var(--text-primary)',
            cursor: 'pointer',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <Folder size={16} />
            Импорт файла
            <input
              type="file"
              accept=".json"
              onChange={handleLoadTemplate}
              style={{ display: 'none' }}
            />
          </label>
        </div>
      </div>

      {/* Scheduled Pipelines Panel */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <button
          onClick={() => setShowScheduledPanel(!showScheduledPanel)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%',
            padding: '12px 16px',
            border: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            color: 'var(--text-primary)',
            fontSize: '14px',
            fontWeight: '600',
          }}
        >
          <Clock size={16} />
          Запланированные пайплайны ({scheduledTemplates.length})
          {showScheduledPanel ? <ChevronUp size={16} style={{ marginLeft: 'auto' }} /> : <ChevronDown size={16} style={{ marginLeft: 'auto' }} />}
        </button>

        {showScheduledPanel && (
          <div style={{ padding: '0 16px 16px' }}>
            {scheduledTemplates.length === 0 ? (
              <p style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>
                Нет запланированных пайплайнов. Сохраните шаблон с включённым расписанием.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {scheduledTemplates.map((tpl) => {
                  const ic = tpl.input_config;
                  const schedule = tpl.schedule || {};
                  const scheduleLabel = schedule.type === 'cron'
                    ? `cron: ${schedule.expr}`
                    : `каждые ${schedule.expr}`;

                  let inputLabel = 'Не задано';
                  let inputDetail = null;

                  if (ic) {
                    if (ic.type === 'manual') {
                      const items = ic.content || [];
                      inputLabel = `Ручной ввод (${items.length} записей)`;
                      inputDetail = items.length > 0 ? items.slice(0, 5).join('\n') + (items.length > 5 ? `\n... +${items.length - 5}` : '') : null;
                    } else if (ic.type === 'config') {
                      const subtypeLabels = { domains: 'Домены', raw: 'Сырой', links: 'Ссылки' };
                      inputLabel = `Config.json: ${subtypeLabels[ic.subtype] || ic.subtype}`;
                      if (ic.tags) inputLabel += ` (теги: ${ic.tags})`;
                    } else if (ic.type === 'mongo') {
                      inputLabel = `MongoDB: ${ic.collection} (${ic.mode})`;
                      const parts = [];
                      if (ic.filter && Object.keys(ic.filter).length > 0) parts.push(`filter: ${JSON.stringify(ic.filter)}`);
                      if (ic.projection && Object.keys(ic.projection).length > 0) parts.push(`projection: ${JSON.stringify(ic.projection)}`);
                      if (ic.pipeline && ic.pipeline.length > 0) parts.push(`pipeline: ${JSON.stringify(ic.pipeline)}`);
                      if (ic.jq_filter) parts.push(`jq: ${ic.jq_filter}`);
                      inputDetail = parts.join('\n');
                    }
                  }

                  return (
                    <div
                      key={tpl._id}
                      style={{
                        padding: '12px',
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        backgroundColor: 'var(--bg-tertiary)',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontWeight: '600', fontSize: '14px', color: 'var(--text-primary)' }}>{tpl.name}</span>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '10px',
                            fontSize: '11px',
                            backgroundColor: '#10b98120',
                            color: '#10b981',
                            fontWeight: '500',
                          }}>
                            {scheduleLabel}
                          </span>
                        </div>
                        <button
                          onClick={() => handleTemplateChange(tpl._id)}
                          style={{
                            padding: '4px 10px',
                            border: '1px solid var(--border-color)',
                            borderRadius: '4px',
                            backgroundColor: 'transparent',
                            color: 'var(--text-secondary)',
                            cursor: 'pointer',
                            fontSize: '12px',
                          }}
                        >
                          Загрузить
                        </button>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px' }}>
                        <div style={{ color: 'var(--text-secondary)' }}>
                          <strong>Входные данные:</strong>{' '}
                          <span style={{ color: ic ? 'var(--text-primary)' : '#ef4444' }}>{inputLabel}</span>
                        </div>
                        {inputDetail && (
                          <pre style={{
                            margin: '4px 0 0',
                            padding: '8px',
                            backgroundColor: 'var(--bg-secondary)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '4px',
                            fontSize: '11px',
                            color: 'var(--text-secondary)',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                            maxHeight: '100px',
                            overflowY: 'auto',
                          }}>
                            {inputDetail}
                          </pre>
                        )}
                        {tpl.last_scheduled_run && (
                          <div style={{ color: 'var(--text-tertiary)', fontSize: '11px', marginTop: '4px' }}>
                            Последний запуск: {new Date(tpl.last_scheduled_run).toLocaleString()}
                          </div>
                        )}
                        {tpl.created_by && (
                          <div style={{ color: 'var(--text-tertiary)', fontSize: '11px' }}>
                            Создан: {tpl.created_by}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Main content */}
      <div style={{ display: 'flex', gap: '16px', height: showScheduledPanel ? 'calc(100% - 400px)' : 'calc(100% - 200px)' }}>
        {/* Tool palette */}
        <div className="card" style={{
          width: '250px',
          padding: '16px',
          overflowY: 'auto',
        }}>
          <h3 style={{
            fontSize: '16px',
            fontWeight: '600',
            marginBottom: '16px',
            color: 'var(--text-primary)',
          }}>
            Доступные инструменты
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {getAllTools().map((tool) => {
              const metadata = TOOL_METADATA[tool];
              return (
                <button
                  key={tool}
                  onClick={() => addToolNode(tool)}
                  style={{
                    padding: '12px',
                    border: `2px solid ${metadata.color}`,
                    borderRadius: '8px',
                    backgroundColor: 'var(--bg-tertiary)',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `${metadata.color}15`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ fontSize: '20px' }}>{metadata.icon}</div>
                    <div>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: 'var(--text-primary)',
                      }}>
                        {metadata.name}
                      </div>
                      <div style={{
                        fontSize: '11px',
                        color: 'var(--text-secondary)',
                        marginTop: '2px',
                      }}>
                        {metadata.description}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div style={{
            marginTop: '24px',
            padding: '12px',
            backgroundColor: 'var(--bg-tertiary)',
            borderRadius: '6px',
          }}>
            <h4 style={{
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '12px',
              color: 'var(--text-primary)',
            }}>
              Источник входных данных
            </h4>
            
            {/* Input Source Type Selection */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' }}>
                {[
                  { value: 'manual', label: 'Ручной ввод' },
                  { value: 'config', label: 'Config.json' },
                  { value: 'tool', label: 'Предыдущий пайплайн' }
                ].map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => handleInputSourceChange(value)}
                    style={{
                      padding: '6px 12px',
                      border: `1px solid ${inputSourceType === value ? '#3b82f6' : '#d1d5db'}`,
                      borderRadius: '4px',
                      backgroundColor: inputSourceType === value ? '#3b82f6' : 'transparent',
                      color: inputSourceType === value ? 'white' : 'var(--text-primary)',
                      cursor: 'pointer',
                      fontSize: '12px',
                      textAlign: 'left',
                    }}
                  >
                    {label}
                  </button>
                ))}
                {/* MongoDB Query Button */}
                <button
                  onClick={() => setShowMongoModal(true)}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #10b981',
                    borderRadius: '4px',
                    backgroundColor: 'transparent',
                    color: '#10b981',
                    cursor: 'pointer',
                    fontSize: '12px',
                    textAlign: 'left',
                    fontWeight: '500',
                  }}
                >
                  🔍 MongoDB-запрос
                </button>
              </div>
            </div>

            {/* Config.json Subtype Selection */}
            {inputSourceType === 'config' && (
              <>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>
                    Формат конфига:
                  </label>
                  <select
                    value={configSubtype}
                    onChange={(e) => handleConfigSubtypeChange(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    <option value="domains">Домены из config.json</option>
                    <option value="raw">Сырой config.json</option>
                    <option value="links">Ссылки из config.json</option>
                  </select>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>
                    Фильтр по тегам (необязательно, через запятую):
                  </label>
                  <input
                    type="text"
                    value={selectedTags}
                    onChange={(e) => handleTagsChange(e.target.value)}
                    placeholder="например: production, critical"
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                    }}
                  />
                </div>
              </>
            )}

            {/* Tool Selection */}
            {inputSourceType === 'tool' && (
              <>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>
                      Выберите пайплайн:
                  </label>
                  <select
                    value={selectedPipeline}
                    onChange={(e) => handlePipelineChange(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    <option value="">Выберите пайплайн...</option>
                    {inputSources?.pipelines?.map((pipeline) => (
                      <option key={pipeline.group_id} value={pipeline.group_id}>
                        {pipeline.name} ({new Date(pipeline.completed_at).toLocaleDateString()})
                      </option>
                    ))}
                  </select>
                </div>
                
                {selectedPipeline && (
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>
                      Выберите инструмент:
                    </label>
                    <select
                      value={selectedTool}
                      onChange={(e) => handleToolChange(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '6px 8px',
                        border: '1px solid var(--border-color)',
                        borderRadius: '4px',
                        fontSize: '12px',
                        backgroundColor: 'var(--bg-secondary)',
                        color: 'var(--text-primary)',
                      }}
                    >
                      <option value="">Выберите инструмент...</option>
                      {inputSources?.pipelines
                        ?.find(p => p.group_id === selectedPipeline)
                        ?.tasks?.map((task) => (
                          <option key={task.tool} value={task.tool}>
                            {task.tool} ({task.data_count} элем.)
                          </option>
                        ))}
                    </select>
                  </div>
                )}
              </>
            )}

            {/* Input Textarea */}
            <div>
              <label style={{ fontSize: '12px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>
                Входные данные:
              </label>
              <textarea
                placeholder={
                  inputSourceType === 'manual'
                    ? "Введите домены, URL или IP (по одному на строку)"
                    : inputSourceType === 'config'
                    ? "Сюда будут загружены хосты из config.json"
                    : "Сюда будут загружены результаты предыдущего пайплайна"
                }
                value={initialInput}
                onChange={(e) => setInitialInput(e.target.value)}
                style={{
                  width: '100%',
                  minHeight: '120px',
                  padding: '8px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '4px',
                  fontSize: '12px',
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  fontFamily: 'monospace',
                  resize: 'vertical',
                }}
              />
            </div>
          </div>
        </div>

        {/* Canvas */}
        <div className="card" style={{ flex: 1, position: 'relative' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onNodeDoubleClick={onNodeDoubleClick}
            nodeTypes={nodeTypes}
            fitView
            nodesDraggable={true}
            nodesConnectable={true}
            elementsSelectable={true}
            onPaneClick={() => {
              setShowNodeConfig(false);
              setSelectedNode(null);
            }}
            style={{
              backgroundColor: 'var(--bg-primary)',
            }}
          >
            <Controls />
            <MiniMap
              style={{
                backgroundColor: 'var(--bg-tertiary)',
              }}
            />
            <Background variant="dots" gap={12} size={1} />
          </ReactFlow>

          {nodes.length === 0 && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              color: 'var(--text-secondary)',
              pointerEvents: 'none',
            }}>
              <Plus size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
              <div style={{ fontSize: '18px', fontWeight: '600' }}>
                Начните с добавления инструментов из левой панели
              </div>
              <div style={{ fontSize: '14px', marginTop: '8px' }}>
                Нажимайте на инструменты для добавления, затем соединяйте совместимые узлы
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Node configuration modal */}
      {showNodeConfig && selectedNode && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <Settings size={24} />
                Настройка: {TOOL_METADATA[selectedNode.data.tool].name}
              </h2>
              <button
                onClick={() => setShowNodeConfig(false)}
                style={{
                  padding: '4px 12px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  cursor: 'pointer',
                }}
              >
                Закрыть
              </button>
            </div>

            <CustomFlagsInput
              tool={selectedNode.data.tool}
              value={nodeOptions}
              onChange={setNodeOptions}
            />

            <div style={{
              display: 'flex',
              gap: '12px',
              marginTop: '24px',
              justifyContent: 'flex-end',
            }}>
              <button
                onClick={() => setShowNodeConfig(false)}
                style={{
                  padding: '10px 20px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Отмена
              </button>
              <button
                onClick={handleSaveNodeConfig}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Сохранить конфигурацию
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Dialog */}
      {showTemplateDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <Save size={24} />
                {templateAction === 'save' ? 'Сохранить шаблон' : 'Обновить шаблон'}
              </h2>
              <button
                onClick={() => setShowTemplateDialog(false)}
                style={{
                  padding: '4px 12px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  cursor: 'pointer',
                }}
              >
                Закрыть
              </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '8px',
                color: 'var(--text-primary)',
              }}>
                Название шаблона:
              </label>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Введите название шаблона"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                }}
                autoFocus
              />
            </div>

            {/* Schedule */}
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: 'var(--bg-tertiary)', borderRadius: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '14px', cursor: 'pointer', marginBottom: templateSchedule.enabled ? '12px' : 0 }}>
                <input type="checkbox" checked={templateSchedule.enabled} onChange={e => setTemplateSchedule({ ...templateSchedule, enabled: e.target.checked })} />
                Запускать по расписанию
              </label>
              {templateSchedule.enabled && (
                <div>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <select value={templateSchedule.type} onChange={e => setTemplateSchedule({ ...templateSchedule, type: e.target.value })} className="form-input" style={{ width: 'auto', padding: '6px 10px' }}>
                      <option value="interval">Интервал</option>
                      <option value="cron">Cron</option>
                    </select>
                    <input
                      className="form-input"
                      value={templateSchedule.expr}
                      onChange={e => setTemplateSchedule({ ...templateSchedule, expr: e.target.value })}
                      placeholder={templateSchedule.type === 'cron' ? '0 3 * * 0' : '24h'}
                      style={{ flex: 1 }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {[{l:'1ч',v:'1h'},{l:'24ч',v:'24h'},{l:'48ч',v:'48h'},{l:'7д',v:'7d'}].map(p => (
                      <button key={p.v} type="button" onClick={() => setTemplateSchedule({ ...templateSchedule, type: 'interval', expr: p.v })}
                        style={{ padding: '3px 8px', fontSize: '11px', borderRadius: '10px', border: '1px solid var(--border-color)',
                          backgroundColor: templateSchedule.type === 'interval' && templateSchedule.expr === p.v ? 'var(--color-primary)' : 'var(--bg-secondary)',
                          color: templateSchedule.type === 'interval' && templateSchedule.expr === p.v ? 'white' : 'var(--text-secondary)', cursor: 'pointer' }}>
                        {p.l}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
            }}>
              <button
                onClick={() => setShowTemplateDialog(false)}
                className="btn btn-secondary"
              >
                Отмена
              </button>
              <button
                onClick={handleConfirmTemplateAction}
                className="btn btn-primary"
              >
                {templateAction === 'save' ? 'Сохранить шаблон' : 'Обновить шаблон'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog for Pipeline Execution */}
      {showConfirmDialog && pipelineToExecute && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '85vh',
            overflow: 'auto',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
            }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                color: 'var(--text-primary)',
              }}>
                <Play size={28} />
                Подтверждение запуска пайплайна
              </h2>
              <button
                onClick={() => {
                  setShowConfirmDialog(false);
                  setPipelineToExecute(null);
                }}
                style={{
                  padding: '6px 14px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                ✕
              </button>
            </div>

            {/* Pipeline Name */}
            <div style={{
              marginBottom: '20px',
              padding: '16px',
              backgroundColor: 'var(--bg-tertiary)',
              borderRadius: '8px',
            }}>
              <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                Название пайплайна:
              </div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>
                {pipelineName || `Пайплайн ${new Date().toLocaleString()}`}
              </div>
            </div>

            {/* Pipeline Stats */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '12px',
              marginBottom: '24px',
            }}>
              <div style={{
                padding: '16px',
                backgroundColor: 'var(--bg-tertiary)',
                borderRadius: '8px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#3b82f6' }}>
                  {pipelineToExecute.length}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                  Всего шагов
                </div>
              </div>
              <div style={{
                padding: '16px',
                backgroundColor: 'var(--bg-tertiary)',
                borderRadius: '8px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#10b981' }}>
                  {nodes.length}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                  Использовано инструментов
                </div>
              </div>
              <div style={{
                padding: '16px',
                backgroundColor: 'var(--bg-tertiary)',
                borderRadius: '8px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#f59e0b' }}>
                  {initialInput.split('\n').filter(line => line.trim()).length}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                  Входных элементов
                </div>
              </div>
            </div>

            {/* Pipeline Visualization */}
            <div style={{
              marginBottom: '24px',
              padding: '20px',
              backgroundColor: 'var(--bg-tertiary)',
              borderRadius: '8px',
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                marginBottom: '16px',
                color: 'var(--text-primary)',
              }}>
                📊 Поток пайплайна
              </h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}>
                {pipelineToExecute.map((step, index) => {
                  const metadata = TOOL_METADATA[step.tool];
                  const hasOptions = step.options && Object.keys(step.options).length > 0;
                  
                  return (
                    <div key={index}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 16px',
                        backgroundColor: 'var(--bg-secondary)',
                        borderRadius: '8px',
                        border: `2px solid ${metadata.color}`,
                      }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          backgroundColor: metadata.color,
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold',
                          fontSize: '14px',
                        }}>
                          {index + 1}
                        </div>
                        <div style={{ fontSize: '24px' }}>{metadata.icon}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)' }}>
                            {metadata.name}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                            {step.input_data.type === 'manual' ? 'Ручной ввод' :
                             step.source_tool ? `Из ${step.source_tool}` : 'Цепочка'}
                            {hasOptions && ` • ${Object.keys(step.options).length} пользовательских флагов`}
                          </div>
                        </div>
                      </div>
                      {index < pipelineToExecute.length - 1 && (
                        <div style={{
                          display: 'flex',
                          justifyContent: 'center',
                          padding: '4px 0',
                        }}>
                          <div style={{
                            width: '2px',
                            height: '16px',
                            backgroundColor: '#3b82f6',
                          }} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Input Preview */}
            <div style={{
              marginBottom: '24px',
              padding: '16px',
              backgroundColor: 'var(--bg-tertiary)',
              borderRadius: '8px',
            }}>
              <h3 style={{
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '12px',
                color: 'var(--text-primary)',
              }}>
                📝 Предпросмотр входных данных (первые 5 элементов)
              </h3>
              <div style={{
                padding: '12px',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: '6px',
                fontFamily: 'monospace',
                fontSize: '12px',
                color: 'var(--text-primary)',
                maxHeight: '120px',
                overflow: 'auto',
              }}>
                {initialInput.split('\n')
                  .filter(line => line.trim())
                  .slice(0, 5)
                  .map((line, idx) => (
                    <div key={idx} style={{ padding: '2px 0' }}>
                      {line}
                    </div>
                  ))}
                {initialInput.split('\n').filter(line => line.trim()).length > 5 && (
                  <div style={{ padding: '2px 0', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                    ... и ещё {initialInput.split('\n').filter(line => line.trim()).length - 5}
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
            }}>
              <button
                onClick={() => {
                  setShowConfirmDialog(false);
                  setPipelineToExecute(null);
                }}
                style={{
                  padding: '12px 24px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '500',
                }}
              >
                Отмена
              </button>
              <button
                onClick={confirmExecutePipeline}
                disabled={executing}
                style={{
                  padding: '12px 32px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  cursor: executing ? 'not-allowed' : 'pointer',
                  fontSize: '15px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: executing ? 0.5 : 1,
                }}
              >
                <Play size={18} />
                {executing ? 'Запуск...' : 'Подтвердить и выполнить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MongoDB Query Builder Modal */}
      <MongoQueryBuilder
        isOpen={showMongoModal}
        onClose={() => setShowMongoModal(false)}
        onConfirm={(results, queryParams) => {
          setInitialInput(results);
          setInputSourceType('manual');
          if (queryParams) {
            setLastMongoQuery(queryParams);
          }
          setShowMongoModal(false);
          showSuccess('Результаты MongoDB-запроса успешно загружены');
        }}
      />
    </div>
  );
};

export default UniversalScan;
